import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ArrowRight, ArrowLeft, RotateCcw } from 'lucide-react';
import { MagneticButton } from './MagneticButton';

const questions = [
  {
    id: 1,
    question: "What's your primary self-care goal?",
    options: [
      { id: 'skin', label: 'Glowing, youthful skin', icon: '✨' },
      { id: 'relax', label: 'Relaxation & stress relief', icon: '🧘' },
      { id: 'hair', label: 'Healthy, beautiful hair', icon: '💇' },
      { id: 'sleep', label: 'Better quality sleep', icon: '😴' },
    ]
  },
  {
    id: 2,
    question: "How much time can you dedicate to self-care daily?",
    options: [
      { id: 'quick', label: '5 minutes or less', icon: '⚡' },
      { id: 'moderate', label: '10-15 minutes', icon: '⏱️' },
      { id: 'dedicated', label: '20+ minutes', icon: '🕐' },
    ]
  },
  {
    id: 3,
    question: "What type of tools do you prefer?",
    options: [
      { id: 'traditional', label: 'Traditional & natural (jade, silk)', icon: '🌿' },
      { id: 'tech', label: 'High-tech devices (LED, massagers)', icon: '🔌' },
      { id: 'both', label: 'A mix of both', icon: '⚖️' },
    ]
  }
];

const productRecommendations = {
  'skin-quick-traditional': {
    primary: 'Jade Roller & Gua Sha Set',
    reason: 'Perfect for a quick 5-minute facial massage to boost circulation and reduce puffiness.',
    secondary: 'Ice Globes Cryo Facial Massager'
  },
  'skin-quick-tech': {
    primary: 'Yellow Silicone Facial Cleanser',
    reason: 'Fast, effective cleansing that fits into any busy morning routine.',
    secondary: 'Brown Silicone Facial Cleanser'
  },
  'skin-quick-both': {
    primary: 'Ice Globes Cryo Facial Massager',
    reason: 'Quick de-puffing that combines natural cryotherapy with modern convenience.',
    secondary: 'Jade Roller & Gua Sha Set'
  },
  'skin-moderate-traditional': {
    primary: 'Jade Roller & Gua Sha Set',
    reason: 'The complete facial ritual for lymphatic drainage and radiant skin.',
    secondary: 'Silk Pillow Cover'
  },
  'skin-moderate-tech': {
    primary: 'Red Light Face Massager',
    reason: 'Advanced light therapy combined with massage for visible skin renewal.',
    secondary: 'Silicone Facial Cleanser'
  },
  'skin-moderate-both': {
    primary: 'Ice Globes Cryo Facial Massager',
    reason: 'Natural cryotherapy for de-puffing, pairs perfectly with your skincare routine.',
    secondary: 'Red Light Face Massager'
  },
  'skin-dedicated-traditional': {
    primary: 'Jade Roller & Gua Sha Set',
    reason: 'Master the art of facial massage with this complete traditional toolkit.',
    secondary: 'Ice Globes Cryo Facial Massager'
  },
  'skin-dedicated-tech': {
    primary: 'Red Light Face Massager',
    reason: 'Professional-level light therapy for comprehensive skin rejuvenation.',
    secondary: 'Wireless Eye Massager'
  },
  'skin-dedicated-both': {
    primary: 'Red Light Face Massager',
    reason: 'Combine with jade rolling for the ultimate skincare ritual.',
    secondary: 'Jade Roller & Gua Sha Set'
  },
  'relax-quick-traditional': {
    primary: 'Ice Globes Cryo Facial Massager',
    reason: 'Quick stress relief through cooling facial massage.',
    secondary: 'Jade Roller & Gua Sha Set'
  },
  'relax-quick-tech': {
    primary: 'Wireless Eye Massager',
    reason: 'Instant relaxation with heat and gentle vibration for tired eyes.',
    secondary: 'Red Light Face Massager'
  },
  'relax-quick-both': {
    primary: 'Wireless Eye Massager',
    reason: 'Perfect quick break - heat therapy and Bluetooth music for instant calm.',
    secondary: 'Ice Globes Cryo Facial Massager'
  },
  'relax-moderate-traditional': {
    primary: 'Jade Roller & Gua Sha Set',
    reason: 'Meditative facial massage ritual for mind-body relaxation.',
    secondary: 'Ice Globes Cryo Facial Massager'
  },
  'relax-moderate-tech': {
    primary: 'Wireless Eye Massager',
    reason: 'Heat therapy with built-in Bluetooth for a complete relaxation experience.',
    secondary: 'Seashell Phototherapy Massage Pillow'
  },
  'relax-moderate-both': {
    primary: 'Wireless Eye Massager',
    reason: 'Modern comfort meets soothing heat therapy.',
    secondary: 'Jade Roller & Gua Sha Set'
  },
  'relax-dedicated-traditional': {
    primary: 'Silk Hair Bonnet',
    reason: 'Complete your relaxation ritual with luxurious silk comfort.',
    secondary: 'Jade Roller & Gua Sha Set'
  },
  'relax-dedicated-tech': {
    primary: 'Seashell Phototherapy Massage Pillow',
    reason: 'Full-body relaxation with red light therapy and deep massage.',
    secondary: 'Wireless Eye Massager'
  },
  'relax-dedicated-both': {
    primary: 'Seashell Phototherapy Massage Pillow',
    reason: 'The ultimate relaxation tool combining massage and light therapy.',
    secondary: 'Wireless Eye Massager'
  },
  'hair-quick-traditional': {
    primary: 'Silk Hair Bonnet - Cream',
    reason: 'Effortless hair protection while you sleep - zero daily effort needed.',
    secondary: 'Silk Hair Bonnet - Caramel'
  },
  'hair-quick-tech': {
    primary: 'Silk Hair Bonnet - Cream',
    reason: 'Even tech lovers appreciate the simplicity of silk hair care.',
    secondary: 'Red Light Face Massager'
  },
  'hair-quick-both': {
    primary: 'Silk Hair Bonnet - Cream',
    reason: 'The easiest upgrade for healthier hair - just wear it to bed!',
    secondary: 'Silk Pillow Cover - Yellow'
  },
  'hair-moderate-traditional': {
    primary: 'Silk Hair Bonnet - Caramel',
    reason: 'Premium silk protection for your beauty sleep hair care routine.',
    secondary: 'Silk Pillow Cover - White'
  },
  'hair-moderate-tech': {
    primary: 'Silk Hair Bonnet - Cream',
    reason: 'Natural silk fiber technology for frizz-free mornings.',
    secondary: 'Silk Pillow Cover - Yellow'
  },
  'hair-moderate-both': {
    primary: 'Silk Hair Bonnet - Caramel',
    reason: 'Combine with a silk pillowcase for the ultimate hair care duo.',
    secondary: 'Silk Pillow Cover - White'
  },
  'hair-dedicated-traditional': {
    primary: 'Silk Hair Bonnet - Cream',
    reason: 'Complete your hair care ritual with pure mulberry silk.',
    secondary: 'Silk Pillow Cover - Yellow'
  },
  'hair-dedicated-tech': {
    primary: 'Silk Hair Bonnet - Caramel',
    reason: 'Even the most advanced hair care starts with protecting what you have.',
    secondary: 'Red Light Face Massager'
  },
  'hair-dedicated-both': {
    primary: 'Silk Hair Bonnet - Cream',
    reason: 'The foundation of any serious hair care routine.',
    secondary: 'Silk Pillow Cover - White'
  },
  'sleep-quick-traditional': {
    primary: 'Silk Pillow Cover - Yellow',
    reason: 'Transform your sleep with zero effort - just change your pillowcase.',
    secondary: 'Silk Hair Bonnet - Cream'
  },
  'sleep-quick-tech': {
    primary: 'Wireless Eye Massager',
    reason: 'Quick pre-sleep relaxation with heat and soothing music.',
    secondary: 'Silk Pillow Cover - White'
  },
  'sleep-quick-both': {
    primary: 'Silk Pillow Cover - White',
    reason: 'The simplest upgrade for better sleep - pure luxury silk.',
    secondary: 'Wireless Eye Massager'
  },
  'sleep-moderate-traditional': {
    primary: 'Silk Pillow Cover - Yellow',
    reason: 'Create a complete silk sleep sanctuary.',
    secondary: 'Silk Hair Bonnet - Cream'
  },
  'sleep-moderate-tech': {
    primary: 'Wireless Eye Massager',
    reason: 'Wind down with heat therapy and Bluetooth before bed.',
    secondary: 'Silk Pillow Cover - Yellow'
  },
  'sleep-moderate-both': {
    primary: 'Silk Pillow Cover - White',
    reason: 'Combine silk comfort with a relaxing eye massage routine.',
    secondary: 'Wireless Eye Massager'
  },
  'sleep-dedicated-traditional': {
    primary: 'Silk Pillow Cover - Yellow',
    reason: 'Build the ultimate silk sleep experience.',
    secondary: 'Silk Hair Bonnet - Caramel'
  },
  'sleep-dedicated-tech': {
    primary: 'Wireless Eye Massager',
    reason: 'Complete sleep preparation with heat therapy and relaxation.',
    secondary: 'Silk Pillow Cover - White'
  },
  'sleep-dedicated-both': {
    primary: 'Silk Pillow Cover - Yellow',
    reason: 'Premium silk paired with modern relaxation technology.',
    secondary: 'Wireless Eye Massager'
  }
};

const getRecommendation = (answers) => {
  const key = `${answers[0]}-${answers[1]}-${answers[2]}`;
  return productRecommendations[key] || productRecommendations['skin-moderate-both'];
};

export const ProductQuiz = () => {
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-3 = questions, 4 = results
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleStart = () => {
    setCurrentStep(1);
    setAnswers([]);
    setSelectedOption(null);
  };

  const handleAnswer = (optionId) => {
    setSelectedOption(optionId);
  };

  const handleNext = () => {
    if (selectedOption) {
      const newAnswers = [...answers, selectedOption];
      setAnswers(newAnswers);
      setSelectedOption(null);
      
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentStep(4);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setAnswers(answers.slice(0, -1));
      setSelectedOption(answers[answers.length - 1] || null);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setAnswers([]);
    setSelectedOption(null);
  };

  const scrollToProducts = () => {
    const element = document.querySelector('#products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const recommendation = currentStep === 4 ? getRecommendation(answers) : null;

  return (
    <section id="quiz" data-testid="quiz-section" className="py-24 lg:py-32 bg-[#F8F6F1] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-10 w-32 h-32 bg-[#DAB668] rounded-full opacity-5 blob" />
      <div className="absolute bottom-20 left-20 w-40 h-40 bg-[#DAB668] rounded-full opacity-5 blob-delayed" />

      <div className="max-w-3xl mx-auto px-6 lg:px-12 relative z-10">
        <AnimatePresence mode="wait">
          {/* Intro */}
          {currentStep === 0 && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 bg-[#DAB668]/10 text-[#DAB668] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles size={16} />
                <span>Personalized Recommendation</span>
              </div>

              <h2 data-testid="quiz-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
                Find Your Perfect Tool
              </h2>

              <p className="text-[#6E6A62] max-w-xl mx-auto mb-10">
                Answer 3 quick questions and we'll recommend the ideal Buttershine product for your self-care journey.
              </p>

              <MagneticButton
                onClick={handleStart}
                data-testid="quiz-start-btn"
                className="px-10 py-4 bg-[#DAB668] text-white rounded-full font-medium hover:bg-[#C5A152] transition-colors inline-flex items-center gap-2"
              >
                Start Quiz
                <ArrowRight size={18} />
              </MagneticButton>
            </motion.div>
          )}

          {/* Questions */}
          {currentStep >= 1 && currentStep <= 3 && (
            <motion.div
              key={`question-${currentStep}`}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
            >
              {/* Progress */}
              <div className="flex items-center justify-center gap-2 mb-8">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      step <= currentStep ? 'w-12 bg-[#DAB668]' : 'w-8 bg-[#DAB668]/20'
                    }`}
                  />
                ))}
              </div>

              <div className="text-center mb-8">
                <span className="text-[#DAB668] text-sm font-medium">Question {currentStep} of 3</span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#2C2C2C] mt-2">
                  {questions[currentStep - 1].question}
                </h3>
              </div>

              {/* Options */}
              <div className="grid gap-4 mb-8">
                {questions[currentStep - 1].options.map((option) => (
                  <motion.button
                    key={option.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleAnswer(option.id)}
                    data-testid={`quiz-option-${option.id}`}
                    className={`w-full p-5 rounded-2xl border-2 transition-all duration-300 flex items-center gap-4 text-left ${
                      selectedOption === option.id
                        ? 'border-[#DAB668] bg-[#DAB668]/10'
                        : 'border-[#E8E4DA] bg-white hover:border-[#DAB668]/50'
                    }`}
                  >
                    <span className="text-2xl">{option.icon}</span>
                    <span className="text-[#2C2C2C] font-medium">{option.label}</span>
                  </motion.button>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-[#6E6A62] hover:text-[#2C2C2C] transition-colors"
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                <MagneticButton
                  onClick={handleNext}
                  disabled={!selectedOption}
                  data-testid="quiz-next-btn"
                  className={`px-8 py-3 rounded-full font-medium transition-all inline-flex items-center gap-2 ${
                    selectedOption
                      ? 'bg-[#DAB668] text-white hover:bg-[#C5A152]'
                      : 'bg-[#E8E4DA] text-[#6E6A62] cursor-not-allowed'
                  }`}
                >
                  {currentStep === 3 ? 'See Results' : 'Next'}
                  <ArrowRight size={18} />
                </MagneticButton>
              </div>
            </motion.div>
          )}

          {/* Results */}
          {currentStep === 4 && recommendation && (
            <motion.div
              key="results"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles size={16} />
                <span>Your Perfect Match</span>
              </div>

              <h3 className="font-serif text-3xl sm:text-4xl text-[#2C2C2C] mb-4">
                {recommendation.primary}
              </h3>

              <p className="text-[#6E6A62] max-w-lg mx-auto mb-6">
                {recommendation.reason}
              </p>

              <div className="bg-white rounded-2xl p-6 mb-8 border border-[#E8E4DA]">
                <p className="text-sm text-[#6E6A62] mb-2">Also consider:</p>
                <p className="font-serif text-xl text-[#2C2C2C]">{recommendation.secondary}</p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <MagneticButton
                  onClick={scrollToProducts}
                  data-testid="quiz-view-products-btn"
                  className="px-8 py-4 bg-[#DAB668] text-white rounded-full font-medium hover:bg-[#C5A152] transition-colors inline-flex items-center gap-2"
                >
                  View Products
                  <ArrowRight size={18} />
                </MagneticButton>

                <button
                  onClick={handleReset}
                  className="px-8 py-4 border-2 border-[#DAB668] text-[#2C2C2C] rounded-full font-medium hover:bg-[#DAB668]/10 transition-colors inline-flex items-center gap-2"
                >
                  <RotateCcw size={18} />
                  Retake Quiz
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ProductQuiz;
